import {
  MDSShortcutKeyComboProps,
  MDSShortcutKeyComboSize,
  MDSShortcutKeyProps,
  MDSShortcutKeyVariant,
} from "./shortcut-key.types";
import {
  SHORTCUT_KEY_FONT_WEIGHT,
  StyledShortcutKeyWrapper,
  StyledShortcutKeyText,
} from "./shortcut-key.style";
import { MDSShortcutKeyCharacterMapping } from "@/mds/components/shortcut-key/shortcut-key.mapping";
import { getShortcutKeyComboDisplayTokens } from "./shortcut-key-combo.utils";
import styled from "@emotion/styled";
import { MDSTextColor } from "@/mds/components/text";

const KEYCAP_SIZE_PX: Record<MDSShortcutKeyComboSize, number> = {
  Default: 16,
  Large: 20,
};

const FONT_SIZE_PX: Record<MDSShortcutKeyComboSize, number> = {
  Default: 11,
  Large: 15,
};

const MIN_TEXT_SCALE = 0.7;
const WIDTH_SAFE_INSET_PX = 2;

const FALLBACK_GLYPH_WIDTH_MULTIPLIER: Record<string, number> = {
  "⇥": 1.4,
  "—": 1.3,
  "–": 1.15,
  "⮐": 1.1,
};

const GLYPH_OPTICAL_CENTER_OFFSET_EM: Partial<Record<string, number>> = {
  "[": -0.05,
  "]": -0.05,
  "⇥": -0.03,
};

let measureContext: CanvasRenderingContext2D | null | undefined;

const getMeasureContext = (): CanvasRenderingContext2D | null => {
  if (typeof document === "undefined") {
    return null;
  }

  if (measureContext !== undefined) {
    return measureContext;
  }

  measureContext = document.createElement("canvas").getContext("2d");
  return measureContext;
};

const getShortcutKeyFontFamily = (): string => {
  if (typeof window === "undefined") {
    return "sans-serif";
  }

  return window.getComputedStyle(document.body).fontFamily || "sans-serif";
};

const getFallbackGlyphWidthPx = ({
  label,
  size,
}: {
  label: string;
  size: MDSShortcutKeyComboSize;
}): number => {
  const widthMultiplier = FALLBACK_GLYPH_WIDTH_MULTIPLIER[label] ?? 0.65;
  return FONT_SIZE_PX[size] * widthMultiplier;
};

const getShortcutKeyMeasureFont = (size: MDSShortcutKeyComboSize): string =>
  `${SHORTCUT_KEY_FONT_WEIGHT} ${FONT_SIZE_PX[size]}px ${getShortcutKeyFontFamily()}`;

const getSingleCharacterTextScale = ({
  label,
  size,
}: {
  label: string;
  size: MDSShortcutKeyComboSize;
}): number => {
  const context = getMeasureContext();
  const availableWidth = KEYCAP_SIZE_PX[size] - WIDTH_SAFE_INSET_PX;
  const fallbackWidth = getFallbackGlyphWidthPx({ label, size });

  const measuredWidth =
    context === null
      ? fallbackWidth
      : (() => {
          context.font = getShortcutKeyMeasureFont(size);
          return Math.max(context.measureText(label).width, fallbackWidth);
        })();

  if (measuredWidth <= availableWidth) {
    return 1;
  }

  return Math.max(MIN_TEXT_SCALE, availableWidth / measuredWidth);
};

const getSingleCharacterTextTranslateY = ({ label }: { label: string }): string | undefined => {
  const offsetEm = GLYPH_OPTICAL_CENTER_OFFSET_EM[label];
  if (offsetEm === undefined) {
    return undefined;
  }

  return `${offsetEm}em`;
};

const MDSShortcutKeyCap = ({
  label,
  inline = false,
  size = "Default",
  color = MDSTextColor.Secondary,
  variant = "Default",
  isFixedSize = false,
  textScale = 1,
  textTranslateY,
}: {
  label: string;
  inline?: boolean;
  size?: MDSShortcutKeyComboProps["size"];
  color?: MDSTextColor;
  variant?: MDSShortcutKeyVariant;
  isFixedSize?: boolean;
  textScale?: number;
  textTranslateY?: string;
}) => {
  return (
    <StyledShortcutKeyWrapper
      $inline={inline}
      $isFixedWidth={isFixedSize}
      $size={size}
      $variant={variant}
    >
      <StyledShortcutKeyText
        color={color}
        $size={size}
        $variant={variant}
        $textScale={textScale}
        $textTranslateY={textTranslateY}
      >
        {label}
      </StyledShortcutKeyText>
    </StyledShortcutKeyWrapper>
  );
};

export const MDSShortcutKey = ({
  character,
  inline = false,
  size = "Default",
  color = MDSTextColor.Secondary,
  variant = "Default",
}: MDSShortcutKeyProps) => {
  const label = MDSShortcutKeyCharacterMapping[character];
  const isFixedSize = variant === "Default" && label.length === 1;
  const textScale = isFixedSize ? getSingleCharacterTextScale({ label, size }) : 1;
  const textTranslateY =
    label.length === 1 ? getSingleCharacterTextTranslateY({ label }) : undefined;

  return (
    <MDSShortcutKeyCap
      inline={inline}
      label={label}
      size={size}
      color={color}
      variant={variant}
      isFixedSize={isFixedSize}
      textScale={textScale}
      textTranslateY={textTranslateY}
    />
  );
};

export const MDSShortcutKeyCombo = ({
  hotkey,
  size = "Default",
  color,
  variant = "Default",
}: MDSShortcutKeyComboProps) => {
  const labels = getShortcutKeyComboDisplayTokens({ hotkey });
  const resolvedColor =
    color ?? (variant === "Tooltip" ? MDSTextColor.Tertiary : MDSTextColor.Secondary);

  return (
    <MDSShortcutKeyGroup $variant={variant}>
      {labels.map((label, index) => {
        const isFixedSize = variant === "Default" && label.length === 1;
        const textScale = isFixedSize ? getSingleCharacterTextScale({ label, size }) : 1;
        const textTranslateY =
          label.length === 1 ? getSingleCharacterTextTranslateY({ label }) : undefined;

        return (
          <MDSShortcutKeyCap
            key={`${hotkey}-${label}-${index}`}
            label={label}
            size={size}
            color={resolvedColor}
            variant={variant}
            isFixedSize={isFixedSize}
            textScale={textScale}
            textTranslateY={textTranslateY}
          />
        );
      })}
    </MDSShortcutKeyGroup>
  );
};

export const MDSShortcutKeyGroup = styled.div<{ $variant?: MDSShortcutKeyVariant }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;
